import { ref, computed, onMounted } from 'vue';
import { useIntervalFn } from '@vueuse/core';

import { useRoute, useRouter } from 'vue-router';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useUserStore } from '@/stores/user';

import { handleError } from '@/services/utils.js';

import { usePermissionsStore } from '@/stores/permissions';
import { useProjectStore } from '@/stores/project';

import Logout from '@/gql/user/Logout.gql';
import SendEmailOtp from '@/gql/user/SendEmailOtp.gql';
import LoginWithEmailOtp from '@/gql/user/LoginWithEmailOtp.gql';

export function useAuth() {
  const route = useRoute();
  const router = useRouter();
  const userStore = useUserStore();
  const permissionsStore = usePermissionsStore();
  const projectStore = useProjectStore();

  const token = computed(() => route.hash.split('#access_token=')[1]);

  const { mutate: logoutMutation, loading: logoutMutationLoading } =
    useMutation(Logout);

  function login(provider) {
    const providerId = provider.toUpperCase();
    window.location = `${import.meta.env.VITE_REST_ENDPOINT}/login?provider=${providerId}&callback=${window.location.origin}/login`;
  }

  function saveAccessToken(token) {
    localStorage.access_token = token;
  }

  async function fetchInitialData() {
    await userStore.fetchUser();
    await permissionsStore.fetchPermissions();
    await projectStore.fetchProjects();
  }

  function redirectAfterLogin() {
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    if (redirectPath && !redirectPath.includes('page-not-found')) {
      router.replace(redirectPath);
    } else {
      router.replace(`/${projectStore.currentProjectId}/dashboard`);
    }
  }

  onMounted(async () => {
    if (userStore.isAuthenticated && route.name === 'Login') {
      await fetchInitialData();
      redirectAfterLogin();
    }

    if (route.hash) {
      const token = route.hash.split('#access_token=')[1];
      if (token) {
        saveAccessToken(token);
        await fetchInitialData();
        redirectAfterLogin();
      } else {
        localStorage.removeItem('access_token');
        router.push('/login');
      }
    }
  });

  return {
    token,
    login,
    saveAccessToken,
    fetchInitialData,
    redirectAfterLogin,
    logoutMutationLoading,
    logoutMutation,
  };
}

// Function to handle email login with OTP
export function useEmailLogin() {
  const { saveAccessToken, fetchInitialData, redirectAfterLogin } = useAuth();

  const activeStep = ref(1);
  const email = ref('');
  const code = ref('');
  const allowResendCode = ref(false);
  const countdownSeconds = ref(60);
  const showSent = ref(false);

  // Computed property to validate email format
  const isEmailValid = computed(() => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email.value);
  });

  const {
    mutate: sendEmailOtp,
    loading: sendEmailOtpLoading,
    error: sendEmailOtpError,
    onDone: sendEmailOtpDone,
  } = useMutation(SendEmailOtp);

  const {
    mutate: loginWithCodeQuery,
    loading: loginWithEmailOtpLoading,
    error: loginWithCodeError,
    onDone: loginWithCodeDone,
  } = useMutation(LoginWithEmailOtp);

  // Function to send OTP code to the provided email
  function sendCode() {
    loginWithCodeError.value = null;
    sendEmailOtp({ email: email.value });

    sendEmailOtpDone(() => {
      activeStep.value = 2;
      allowResendCode.value = false;
      showSent.value = true;
      setTimeout(() => {
        showSent.value = false;
        countdownSeconds.value = 10;
        resume();
      }, 5000);
    });
  }

  // Function to login using the OTP code
  const loadingInitialData = ref(false);
  function loginWithCode() {
    const variables = {
      email: email.value,
      otp: parseInt(code.value),
    };
    loginWithCodeQuery(variables);

    loginWithCodeDone(async (data) => {
      const { token } = data.data;
      if (token) {
        localStorage.access_token = token;
        try {
          loadingInitialData.value = true;
          saveAccessToken(token);
          await fetchInitialData();
          redirectAfterLogin();
        } catch (error) {
          handleError(error);
        } finally {
          loadingInitialData.value = false;
        }
      }
    });
  }

  const loginWithCodeLoading = computed(() => {
    return loginWithEmailOtpLoading.value || loadingInitialData.value;
  });

  // Add an interval for countdown
  const { pause, resume } = useIntervalFn(() => {
    countdownSeconds.value -= 1;
    if (countdownSeconds.value === 0) {
      pause();
      allowResendCode.value = true;
    }
  }, 1000);

  return {
    activeStep,
    email,
    isEmailValid,
    sendCode,
    sendEmailOtpLoading,
    sendEmailOtpError,
    code,
    countdownSeconds,
    showSent,
    loginWithCode,
    loginWithCodeLoading,
    loginWithCodeError,
  };
}
